import React from "react";
import { Link } from "react-router-dom";
import {
  FaCalendarCheck,
  FaArrowRight,
  FaPercent,
  FaExchangeAlt,
  FaWrench,
  FaSuitcaseRolling,
  FaHome,
  FaCarCrash,
  FaMoneyBillWave,
  FaRegCheckCircle,
} from "react-icons/fa";
// Ensure you have an appropriate image or use a solid background fallback
import heroImage from "../../assets/react-cars-hero-bg.jpg";

const useCases = [
  {
    icon: <FaSuitcaseRolling />,
    title: "Corporate & Extended Travel",
    desc: "Perfect for temporary work assignments, film crews, or consultants who need reliable transportation for 1 to 11 months without committing to a lease.",
  },
  {
    icon: <FaHome />,
    title: "Snowbirds & Seasonal Living",
    desc: "Heading south for the winter? Skip the wear-and-tear of driving your own car cross-country and pick up a fresh, maintained vehicle when you arrive.",
  },
  {
    icon: <FaCarCrash />,
    title: "Auto Repair Replacements",
    desc: "Waiting on parts or dealing with a lengthy insurance claim? Keep your life moving with a discounted long-term rental until your primary vehicle is ready.",
  },
];

const faqs = [
  {
    q: "How am I billed for a multi-month rental?",
    a: "Instead of being charged all at once, long-term rentals are typically billed in 30-day increments. Your credit card on file will be automatically charged at the beginning of each 30-day cycle.",
  },
  {
    q: "Can I swap the vehicle if my needs change?",
    a: "Yes! One of the biggest benefits of long-term renting is flexibility. If you need a larger SUV for a weekend or just want to switch things up, you can visit a local branch to swap your vehicle (subject to availability and rate changes).",
  },
  {
    q: "Are there mileage limits on month-long rentals?",
    a: "Many long-term rental contracts include generous or unlimited mileage policies, but this varies by rental agency and vehicle class. Always check the specific mileage terms during the checkout process.",
  },
  {
    q: "Do I have to pay for oil changes and maintenance?",
    a: "No. Routine maintenance (like oil changes and tire rotations) is fully covered. When a service light comes on, simply bring the car to a local branch and they will either service it or swap you into a fresh vehicle.",
  },
];

export default function LongTermCarRental() {
  return (
    <div className="min-h-screen bg-slate-50 font-sans text-slate-900">
      {/* HERO SECTION */}
      <section className="relative flex min-h-[85vh] items-center justify-center overflow-hidden bg-slate-900 px-4 py-20 sm:px-6">
        <div className="absolute inset-0 z-0">
          <img
            src={heroImage}
            alt="Long Term Car Rental"
            className="h-full w-full object-cover object-center opacity-40 grayscale"
          />
          {/* Emerald tinted gradient overlay */}
          <div className="absolute inset-0 bg-gradient-to-b from-slate-900/90 via-emerald-900/70 to-slate-900" />
        </div>

        <div className="relative z-10 mx-auto max-w-4xl text-center">
          <div className="mb-6 flex justify-center">
            <span className="inline-flex items-center gap-2 rounded-full border border-emerald-400/30 bg-emerald-500/10 px-4 py-1.5 text-xs font-black uppercase tracking-[0.2em] text-emerald-400 backdrop-blur-md">
              <FaCalendarCheck className="text-emerald-400" /> 30+ Day Rentals
            </span>
          </div>
          <h1 className="mb-6 text-5xl font-black leading-tight tracking-tight text-white sm:text-6xl lg:text-7xl">
            Rent Longer. <br className="hidden sm:block" />
            <span className="text-transparent bg-clip-text bg-gradient-to-r from-emerald-300 to-emerald-500">
              Save More.
            </span>
          </h1>
          <p className="mx-auto mb-10 max-w-2xl text-lg font-medium leading-relaxed text-slate-300 sm:text-xl">
            Whether for a temporary work assignment, wintering away from home,
            or waiting on a new car delivery. Enjoy deep discounts on rentals of
            30 days or more.
          </p>
          <div className="flex flex-col items-center justify-center gap-4 sm:flex-row">
            <Link
              to="/cars"
              className="group flex w-full items-center justify-center gap-3 rounded-full bg-emerald-500 px-8 py-4 text-base font-black text-slate-900 shadow-[0_0_30px_rgba(16,185,129,0.3)] transition-all duration-300 hover:-translate-y-1 hover:bg-emerald-400 hover:shadow-[0_0_50px_rgba(16,185,129,0.5)] sm:w-auto"
            >
              Get Monthly Rates
              <FaArrowRight className="transition-transform group-hover:translate-x-1" />
            </Link>
          </div>
        </div>
      </section>

      {/* TRUST BANNER */}
      <section className="border-b border-slate-200 bg-white py-8">
        <div className="mx-auto flex max-w-7xl flex-wrap items-center justify-center gap-8 px-4 sm:gap-16 sm:px-6">
          {[
            { icon: <FaRegCheckCircle />, text: "No Long-Term Leases" },
            { icon: <FaWrench />, text: "Maintenance Included" },
            { icon: <FaMoneyBillWave />, text: "No Early Return Penalties" },
          ].map((item, idx) => (
            <div key={idx} className="flex items-center gap-3 text-slate-600">
              <div className="text-xl text-emerald-600">{item.icon}</div>
              <span className="text-sm font-bold uppercase tracking-wide">
                {item.text}
              </span>
            </div>
          ))}
        </div>
      </section>

      {/* VALUE PROPOSITION GRID */}
      <section className="mx-auto max-w-7xl px-4 py-20 sm:px-6 lg:py-24">
        <div className="mb-16 text-center">
          <h2 className="text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
            Benefits of Long-Term Rentals
          </h2>
          <p className="mx-auto mt-4 max-w-2xl text-base font-medium text-slate-500">
            Enjoy the perks of car ownership without the depreciation,
            maintenance costs, or long-term commitments.
          </p>
        </div>

        <div className="grid gap-8 md:grid-cols-3">
          {[
            {
              icon: <FaPercent />,
              title: "Deeply Discounted Rates",
              desc: "The longer you rent, the lower your daily rate becomes. Agencies offer their most aggressive pricing for 30+ day commitments.",
            },
            {
              icon: <FaExchangeAlt />,
              title: "Ultimate Flexibility",
              desc: "Extend your rental easily month-to-month, swap vehicles if your needs change, or return the car with no early termination fees.",
            },
            {
              icon: <FaWrench />,
              title: "Zero Maintenance Costs",
              desc: "Routine maintenance, oil changes, and 24/7 roadside assistance are fully covered. Just add fuel and drive with peace of mind.",
            },
          ].map((feature, idx) => (
            <div
              key={idx}
              className="group rounded-[2rem] border border-slate-200 bg-white p-8 shadow-[0_8px_30px_rgb(0,0,0,0.04)] transition-all duration-300 hover:-translate-y-1 hover:border-emerald-200 hover:shadow-[0_20px_40px_rgba(16,185,129,0.08)]"
            >
              <div className="mb-6 flex h-16 w-16 items-center justify-center rounded-2xl bg-emerald-50 text-3xl text-emerald-600 transition-transform duration-300 group-hover:scale-110 group-hover:bg-emerald-500 group-hover:text-white">
                {feature.icon}
              </div>
              <h3 className="mb-3 text-xl font-black text-slate-900">
                {feature.title}
              </h3>
              <p className="text-sm font-medium leading-relaxed text-slate-500">
                {feature.desc}
              </p>
            </div>
          ))}
        </div>
      </section>

      {/* USE CASES */}
      <section className="bg-slate-900 py-20 text-white sm:py-24">
        <div className="mx-auto max-w-7xl px-4 sm:px-6">
          <div className="mb-16 text-center">
            <h2 className="text-3xl font-black tracking-tight text-white sm:text-4xl">
              Who is this for?
            </h2>
          </div>

          <div className="grid gap-6 md:grid-cols-3">
            {useCases.map((useCase, idx) => (
              <div
                key={idx}
                className="flex flex-col justify-start rounded-[2rem] border border-white/10 bg-white/5 p-8 transition-colors hover:border-emerald-500/50 hover:bg-white/10"
              >
                <div className="mb-6 text-4xl text-emerald-400">
                  {useCase.icon}
                </div>
                <h3 className="mb-4 text-2xl font-bold text-white">
                  {useCase.title}
                </h3>
                <p className="text-sm font-medium leading-relaxed text-slate-400">
                  {useCase.desc}
                </p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* FAQ SECTION */}
      <section className="bg-white py-20 sm:py-24">
        <div className="mx-auto max-w-4xl px-4 sm:px-6">
          <h2 className="mb-10 text-center text-3xl font-black tracking-tight text-slate-900 sm:text-4xl">
            Long-Term Rental FAQs
          </h2>
          <div className="space-y-4">
            {faqs.map((faq, idx) => (
              <details
                key={idx}
                className="group rounded-2xl border border-slate-200 bg-[#f8f9fc] p-6 [&_summary::-webkit-details-marker]:hidden"
              >
                <summary className="flex cursor-pointer items-center justify-between font-bold text-slate-900">
                  {faq.q}
                  <span className="ml-4 flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-white text-emerald-600 shadow-sm transition-transform group-open:rotate-180">
                    <svg
                      className="h-4 w-4"
                      fill="none"
                      viewBox="0 0 24 24"
                      stroke="currentColor"
                    >
                      <path
                        strokeLinecap="round"
                        strokeLinejoin="round"
                        strokeWidth="2"
                        d="M19 9l-7 7-7-7"
                      />
                    </svg>
                  </span>
                </summary>
                <p className="mt-4 text-sm font-medium leading-relaxed text-slate-600">
                  {faq.a}
                </p>
              </details>
            ))}
          </div>
        </div>
      </section>

      {/* BOTTOM CTA */}
      <section className="bg-emerald-900 py-16 text-center text-white sm:py-20">
        <div className="mx-auto max-w-3xl px-4">
          <h2 className="mb-6 text-3xl font-black sm:text-4xl">
            Lock in your monthly rate
          </h2>
          <p className="mb-8 text-lg font-medium text-emerald-100">
            Select dates of 30 days or more in our search tool to automatically
            access our partners' long-term discount pricing.
          </p>
          <Link
            to="/cars"
            className="inline-flex items-center gap-2 rounded-full bg-emerald-500 px-8 py-4 font-black text-slate-900 shadow-lg shadow-emerald-500/20 transition-transform hover:-translate-y-1 hover:bg-emerald-400 hover:shadow-xl hover:shadow-emerald-500/30 active:translate-y-0"
          >
            Search Monthly Rentals
            <FaArrowRight />
          </Link>
        </div>
      </section>
    </div>
  );
}
